import Highcharts from "highcharts";
import HighchartsMore from "highcharts/highcharts-more";
import HighchartsStock from "highcharts/modules/stock";
import HighchartsHeatmap from "highcharts/modules/heatmap";
import HighchartsBoost from "highcharts/modules/boost";
// @ts-expect-error
import HC_rounded from "highcharts-rounded-corners";
import HighchartsExporting from "highcharts/modules/exporting";
import HighchartsExportData from "highcharts/modules/export-data";
import HighchartsExportDataOffline from "highcharts/modules/offline-exporting";
import HighchartsBellCurve from "highcharts/modules/histogram-bellcurve";
import HighchartsTreemap from "highcharts/modules/treemap";
import HighchartsDragPanes from "highcharts/modules/drag-panes";
import { useState, useCallback, useRef, useEffect, useLayoutEffect } from "react";
import { findMap, findMapLast } from "$root/utils";

HighchartsTreemap(Highcharts);
HighchartsDragPanes(Highcharts);
HighchartsBellCurve(Highcharts);
HighchartsExporting(Highcharts);
HighchartsExportData(Highcharts);
HighchartsExportDataOffline(Highcharts);
HC_rounded(Highcharts);
HighchartsStock(Highcharts);
HighchartsHeatmap(Highcharts);
HighchartsMore(Highcharts);
HighchartsBoost(Highcharts);

// Ref: https://github.com/highcharts/highcharts/issues/15031#issuecomment-778148724
Highcharts.AST.allowedAttributes.push("data-text-type");

/**
 * Animation off, once, for every chart in the app.
 *
 * Nothing here is decorative-only: an animated frame is a *redraw per frame*, and the charts that cost the
 * most to redraw are exactly the ones a user watches move. A stacked column of 45 portfolios over a dozen
 * breakdown categories is ~1200 points across ~26 series, and it gets re-laid-out on every frame of three
 * separate default animations:
 *
 * - `chart.animation` (500ms) covers redraw and `setSize`, i.e. every reflow. The container observer below
 *   reflows a chart once per frame while a side panel or a pane split transitions its width, so the
 *   animations of consecutive reflows overlap and each one is re-tweening marks the next frame supersedes.
 * - `plotOptions.series.animation` is the entry animation, paid again on every `Series#update` — and
 *   Highcharts' React wrapper updates a chart whenever its options object changes identity.
 * - `states.hover` / `states.inactive` (50ms each) are the ones that surprise: hovering *one* point puts
 *   every other series into `inactive` and animates its group opacity, so drawing the pointer across a
 *   stack re-animates every mark in the chart once per segment crossed.
 *
 * Only the animations are switched off, not the states themselves: the inactive dim still happens, it just
 * arrives at once. Set here rather than per chart so a new chart cannot be born slow, and as
 * `setOptions` defaults rather than as options, so any chart with a real reason to animate can still say so.
 */
Highcharts.setOptions({
	chart: { animation: false },
	plotOptions: {
		series: {
			animation: false,
			states: {
				hover: { animation: { duration: 0 } },
				inactive: { animation: { duration: 0 } },
			},
		},
	},
	tooltip: { animation: false },
});

// Highcharts only auto-reflows on window resize. Observe each chart's container so charts also
// reflow when their parent resizes via CSS/flex changes (e.g. panel splits, side panels).
if (typeof window !== "undefined" && "ResizeObserver" in window) {
	Highcharts.addEvent(Highcharts.Chart, "load", function (this: Highcharts.Chart) {
		const container = (this as unknown as { renderTo?: HTMLElement }).renderTo;
		if (!container) {
			return;
		}
		let frame = 0;
		const ro = new ResizeObserver(() => {
			cancelAnimationFrame(frame);
			frame = requestAnimationFrame(() => {
				if ((this as unknown as { renderTo?: HTMLElement }).renderTo) {
					this.reflow();
				}
			});
		});
		ro.observe(container);
		Highcharts.addEvent(this, "destroy", () => {
			cancelAnimationFrame(frame);
			ro.disconnect();
		});
	});
}

export { Highcharts };

/** Options for {@link useHighchartsDiscretizedZoom}. */
export type UseHighchartsDiscretizedZoomOptions = {
	/**
	 * When `true`, re-baseline the zoom window to `initialDateRange` whenever its span changes by value
	 * (e.g. switching to a scenario whose series covers a different period). Off by default so the
	 * `initialDateRange` argument keeps its "seed once on mount" contract for existing consumers.
	 */
	resyncOnRangeChange?: boolean;
};

export function useHighchartsDiscretizedZoom(
	initialDateRange: { min: number; max: number },
	opts?: UseHighchartsDiscretizedZoomOptions,
): {
	dateRange: { min: number; max: number };
	afterSetExtremes: (this: Highcharts.Axis, event: { userMax?: number; userMin?: number }) => void;
};
export function useHighchartsDiscretizedZoom(
	initialDateRange?: { min: number; max: number },
	opts?: UseHighchartsDiscretizedZoomOptions,
): {
	dateRange: { min: number; max: number } | undefined;
	afterSetExtremes: (this: Highcharts.Axis, event: { userMax?: number; userMin?: number }) => void;
};

export function useHighchartsDiscretizedZoom(
	initialDateRange?: { min: number; max: number },
	opts?: UseHighchartsDiscretizedZoomOptions,
): {
	dateRange: { min: number; max: number } | undefined;
	afterSetExtremes: (this: Highcharts.Axis, event: { userMax?: number; userMin?: number }) => void;
} {
	const [dateRange, setDateRange] = useState<{ min: number; max: number } | undefined>(initialDateRange);
	const isSettingExtremesRef = useRef(false);

	// Opt-in: re-sync the zoom window when the underlying series span actually changes (e.g. switching to
	// a scenario whose performance covers a different period). The `useState` initializer only runs on
	// mount, so without this the window stays pinned to the first series — and the chart's normalization,
	// which rebases against `dateRange.min` and zeroes any point before it, flattens the whole line to 0
	// when the new series starts earlier than the stale window. Compared by value (not reference) so
	// routine refetches that return the same span don't clobber a user's manual zoom.
	const resyncOnRangeChange = opts?.resyncOnRangeChange ?? false;
	const syncedRangeRef = useRef(initialDateRange);
	useEffect(() => {
		if (!resyncOnRangeChange || !initialDateRange) {
			return;
		}
		const prev = syncedRangeRef.current;
		if (prev?.min !== initialDateRange.min || prev?.max !== initialDateRange.max) {
			syncedRangeRef.current = initialDateRange;
			setDateRange(initialDateRange);
		}
	}, [resyncOnRangeChange, initialDateRange]);
	const afterSetExtremes = useCallback(function (this: Highcharts.Axis, event: { userMax?: number; userMin?: number }) {
		if (isSettingExtremesRef.current || event.userMax == null || event.userMin == null) return;

		const points = this.series.at(0)?.points ?? [];
		const discreteMin =
			findMap(points, (p, i, arr) => (p && p.x > event.userMin! ? [arr[i - 1] ?? p] : []))?.x ?? event.userMin;
		const discreteMax = findMapLast(points, (p) => (p && p.x <= event.userMax! ? [p] : []))?.x ?? event.userMax;

		setDateRange((prev) =>
			prev?.min === discreteMin && prev?.max === discreteMax ? prev : { min: discreteMin, max: discreteMax },
		);

		isSettingExtremesRef.current = true;
		this.setExtremes(discreteMin, discreteMax, true, false);
		isSettingExtremesRef.current = false;
	}, []);

	return { dateRange, afterSetExtremes };
}

// HighchartsReact calls chart.update() in a no-dep useLayoutEffect after every render, which triggers
// rangeSelector.render() and resets all button setState values. Call this hook in the parent component
// (i.e. the component that renders HighchartsReact) with the index of the button that should stay
// selected. Because child layout effects run before parent layout effects, our re-application always
// runs after the reset, restoring the correct highlight before the browser paints.
export function useHighchartsReapplyButtonState(
	chartRef: React.RefObject<{ chart: unknown } | null>,
	selectedButtonIndexRef: React.RefObject<number | null>,
) {
	const apply = useCallback(() => {
		const idx = selectedButtonIndexRef.current;
		if (idx === null) return;
		const chart = chartRef.current?.chart;
		if (!chart) return;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const buttons = ((chart as any).rangeSelector?.buttons ?? []) as Array<{ setState(s: number): void }>;
		buttons.forEach((btn, i) => {
			btn.setState(i === idx ? 2 : 0);
		});
	}, [chartRef, selectedButtonIndexRef]);

	useLayoutEffect(apply);

	return apply;
}
